import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useGetScoreCriteriaQuery, useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

import { computeSegmentsUsed } from "./leadScoringSegmentsUsed";

export interface LeadScoringStatus {
	isScoreDone: "red" | "yellow" | "green" | undefined;
	isScoreDoneText: React.ReactNode;
}

/**
 * Derives the Lead Scoring nav bubble (red / yellow / green) and its explanatory
 * tooltip from the company's score + criteria configuration. Extracted from the
 * Sidebar so the nav shell stays lean.
 */
export const useLeadScoringStatus = (): LeadScoringStatus => {
	const { t } = useTranslation();

	const { data: score, isError: isScoreError } = useGetScoreQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});

	const { data: scoreCriteria, isError: isScoreCriteriaError } = useGetScoreCriteriaQuery();

	const isSegmentsUsed = useMemo(
		() => computeSegmentsUsed(score, scoreCriteria),
		[score, scoreCriteria]
	);

	const isScoreDone = useMemo<LeadScoringStatus["isScoreDone"]>(() => {
		const active = scoreCriteria?.allCriteria?.some((criteria) => criteria.locked);
		const isAllActive = !scoreCriteria?.allCriteria?.every((criteria) => criteria.locked);

		const {
			isPersonaLinkedToAnySegment,
			isProspectCompanyLinkedToPersona,
			isPartnerCompanyLinkedToPersona,
			isSegmentsCombinedValid,
		} = isSegmentsUsed;

		const allInUse =
			isSegmentsCombinedValid &&
			isPersonaLinkedToAnySegment &&
			isProspectCompanyLinkedToPersona &&
			isPartnerCompanyLinkedToPersona;

		if (isAllActive) {
			return "red";
		}

		if (score?.migrationFlag && (!active || !allInUse)) {
			return "yellow";
		}

		if (score?.migrationFlag && active && allInUse) {
			return "green";
		}

		return undefined;
	}, [score, scoreCriteria, isSegmentsUsed, isScoreError, isScoreCriteriaError]);

	const isScoreDoneText = useMemo<React.ReactNode>(() => {
		const active = scoreCriteria?.allCriteria?.some((criteria) => criteria.locked);

		const {
			isProspectCompanyInUseLeadScoring,
			isPartnerCompanyInUseLeadScoring,
			isProspectPersonaInUseLeadScoring,
			isPersonaLinkedToAnySegment,
			isProspectCompanyLinkedToPersona,
			isPartnerCompanyLinkedToPersona,
			isSegmentsCombinedValid,
		} = isSegmentsUsed;

		if (isScoreDone === "red") {
			return t("leadScoring.redText");
		}

		if (isScoreDone === "yellow") {
			const missingParts: string[] = [];

			if (!active) {
				missingParts.push(t("leadScoring.yellowText"));
			}

			if (!isPersonaLinkedToAnySegment) {
				missingParts.push(t("leadScoring.personaNotLinkedToAnySegment"));
			}

			// Only warn about segments if none of the groups are valid
			if (!isSegmentsCombinedValid) {
				if (!isProspectCompanyInUseLeadScoring) {
					missingParts.push(t("leadScoring.missingCompanySegment"));
				}

				if (!isPartnerCompanyInUseLeadScoring) {
					missingParts.push(t("leadScoring.missingPartnerSegment"));
				}

				if (!isProspectPersonaInUseLeadScoring) {
					missingParts.push(t("leadScoring.missingPersonaSegment"));
				}
			}

			if (!isProspectCompanyLinkedToPersona) {
				missingParts.push(t("leadScoring.companyNotLinkedToPersona"));
			}

			if (!isPartnerCompanyLinkedToPersona) {
				missingParts.push(t("leadScoring.partnerNotLinkedToPersona"));
			}

			if (missingParts.length > 0) {
				return (
					<div className="flex flex-col gap-2">
						{missingParts.map((part, i) => (
							<span key={i}>{part}</span>
						))}
					</div>
				);
			}
		}

		if (isScoreDone === "green") {
			return t("leadScoring.greenText");
		}

		return undefined;
	}, [t, isScoreDone, isSegmentsUsed, scoreCriteria]);

	return { isScoreDone, isScoreDoneText };
};
